'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Plus, LayoutGrid, Grid2x2Plus } from 'lucide-react';
import { CreateMenu } from '@/components/create-menu';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { useTranslation } from '@/components/i18n-provider';
import { useWorkspaceAnon } from '@/components/workspace/anon-context';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { makeFetchWithGuestMint } from '@/lib/api/fetch-with-guest-mint';
import { startGuest } from '@/lib/auth/guest-client';
import { executeTurnstile } from '@/lib/auth/turnstile-execute';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { cn } from '@/lib/utils';

/**
 * Open the top-of-screen Search-everything popover with the type
 * filter preset to either `agents` or `widgets`. The search popover
 * listens for `workspace:open-search` events (see
 * components/workspace/search-everything.tsx) and, when the `detail`
 * carries a `type`, applies it before focusing the input. This keeps
 * the FAB Agent / Widget items as a search-or-create funnel rather
 * than a direct-spawn menu — typing finds matches, and the Create
 * row at the top opens the user's own personal agent (resolved by
 * identity through `fetchPersonalAgent`) with the right prefill.
 */
function openSearchWith(type: 'agents' | 'widgets') {
  window.dispatchEvent(
    new CustomEvent('workspace:open-search', { detail: { type } }),
  );
}

export function CreateFab({ className }: { className?: string }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { anonReadOnly, sourceWorkspaceId } = useWorkspaceAnon();
  const { openHref } = useFullscreenNav();
  // Wrap the local fetch so a "New workspace" action that hits a
  // structured 401 mints a guest. For an authed/guest user it's a
  // plain fetch. Other items (Agent / Widget) open the in-canvas
  // Search popover and don't need a fetch.
  const fetchGated = useMemo(() => makeFetchWithGuestMint(), []);

  // A true-anon viewing a public canvas they don't own can't write to
  // it — so the FAB mints an ephemeral guest that clones this canvas
  // and drops them onto their own writable copy. If the mint is
  // refused (rate limit in prod) we fall back to /signup.
  // Anyone who arrived via the splash "Open app" is already a guest
  // (anonReadOnly === false), so they never hit this path.
  //
  // The challenge runs on the press. Sending no token at all made this
  // fall back to /signup EVERY time in production, because the mint
  // endpoint refuses a tokenless request wherever the secret is set.
  const anonHandler = async () => {
    const turnstileToken = await executeTurnstile().catch(() => null);
    const r = await startGuest({
      sourceWorkspaceId: sourceWorkspaceId ?? undefined,
      turnstileToken,
    });
    if (r.ok && r.workspaceId) {
      window.location.assign(`/workspace/${r.workspaceId}`);
    } else {
      openHref('/signup');
    }
  };

  return (
    <CreateMenu
      className={cn('pointer-events-auto', className)}
      placement="above"
      align="start"
      portal
      analyticsNamespace="workspace-create-fab"
      // Hide ALL default menu items — they route to /create which is
      // being retired. We re-add the marketplace + workspace items
      // below.
      hiddenDefaults={['agent', 'post', 'channel', 'chat']}
      // The "+" FAB is a three-item launcher: Agent, Widget, Workspace.
      //
      // Agent / Widget no longer create anything directly — they open
      // the Search-everything popover prefiltered to the matching
      // type. From there the user can either pick an existing entry
      // (which adds it to the canvas via the per-row Grid+ button) or
      // hit the "Create agent" / "Create widget" CTA at the top of the
      // list to hand off to their own personal agent with the right
      // prefill (it builds apps and agents itself).
      //
      // The legacy Local shell / SSH / Python shortcuts have been
      // removed — those are now seeded as widget posts (see
      // scripts/seed-system-widgets.ts) and discoverable from the same
      // Search/Discover surfaces as community widgets. One menu item
      // per spawn route, not three special cases.
      extraItems={[
        {
          id: 'agent',
          label: t('workspace.canvas.agent'),
          icon: <Bot className="h-4 w-4" />,
          onClick: anonReadOnly ? anonHandler : () => openSearchWith('agents'),
        },
        {
          id: 'widget',
          label: t('workspace.canvas.widget'),
          icon: <Grid2x2Plus className="h-4 w-4" />,
          onClick: anonReadOnly ? anonHandler : () => openSearchWith('widgets'),
        },
        {
          id: 'workspace',
          label: t('workspace.canvas.workspace'),
          icon: <LayoutGrid className="h-4 w-4" />,
          onClick: anonReadOnly
            ? anonHandler
            : async () => {
                try {
                  const res = await fetchGated('/api/workspaces', { method: 'POST' });
                  if (!res.ok) {
                    // Hit the per-plan workspace cap → open the Plans dialog.
                    if (res.status === 403) {
                      const j = await res.clone().json().catch(() => null);
                      if (j?.code === 'upgrade_required') openPlansDialog();
                    }
                    return;
                  }
                  const data = await res.json();
                  if (data?.workspace?.id) {
                    emitWorkspaceListChanged();
                    router.push(`/workspace/${data.workspace.id}`);
                  }
                } catch {
                  // Either a network error or a SignupRequiredError.
                  // The gate handles the dialog UX; we just drop
                  // the action.
                }
              },
        },
      ]}
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label={open ? t('fab.closeCreateMenu') : t('fab.openCreateMenu')}
          aria-pressed={open}
          // E2E tests target this FAB via `workspace-create-fab` —
          // setting it explicitly here keeps the testid stable across
          // the menu's internal restructuring.
          data-testid="workspace-create-fab"
          // Onboarding overlay anchor — the sketch arrow `create
          // anything` annotation locks onto this rect via
          // `getBoundingClientRect` + ResizeObserver. Don't move the
          // attribute off the button or the arrow will follow the
          // wrong target after a reflow.
          data-onboarding-anchor="fab"
          className={cn(
            'h-14 w-14 rounded-full inline-flex items-center justify-center transition-all select-none liquid-glass',
            !open && 'active:scale-95 hover:shadow-[0_6px_24px_rgba(0,0,0,0.14)]',
            open && 'ws-fab-pressed',
          )}
        >
          <Plus
            className={cn(
              'h-7 w-7 transition-transform duration-200',
              open && 'rotate-45',
            )}
            strokeWidth={1.5}
          />
        </button>
      )}
    />
  );
}
